import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useMsal } from "@azure/msal-react";
import { appServicesLogout, getUsername, checkLoggedIn, loginRequest, getRedirectUri } from "../../authConfig";
import abbLogo from "../../assets/abb-logo.svg";
import { HistoryPanelProvider, useHistoryPanel } from "../../historyPanelContext";
import { ReportProblemButton } from "../../components/ReportProblemButton";


const LayoutContent = () => {
    const { t } = useTranslation();
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const [username, setUsername] = useState("");
    const [loggedIn, setLoggedIn] = useState(false);
    const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
    const avatarMenuRef: RefObject<HTMLDivElement> = useRef(null);
    const { isHistoryPanelOpen, toggleHistoryPanel } = useHistoryPanel();

    useEffect(() => {
        const fetchUser = async () => {
            setUsername((await getUsername(instance)) ?? "");
            setLoggedIn(!!instance.getActiveAccount());
        };
        fetchUser();
    }, []);

    const getInitials = (name: string): string => {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        if (parts[0]) return parts[0].substring(0, 2).toUpperCase();
        return "U";
    };

    const handleLogout = () => {
        setAvatarMenuOpen(false);
        if (activeAccount) {
            instance
                .logoutPopup({
                    mainWindowRedirectUri: "/",
                    account: instance.getActiveAccount()
                })
                .catch(error => console.log(error))
                .then(async () => {
                    setLoggedIn(false);
                    setUsername("");
                });
        } else {
            appServicesLogout();
        }
    };

    const handleLogin = () => {
        instance
            .loginPopup({
                ...loginRequest,
                redirectUri: getRedirectUri()
            })
            .catch(error => console.log(error))
            .then(async () => {
                setLoggedIn(!!instance.getActiveAccount());
                setUsername((await getUsername(instance)) ?? "");
            });
    };

    const handleClickOutsideAvatar = (event: MouseEvent) => {
        if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target as Node)) {
            setAvatarMenuOpen(false);
        }
    };

    useEffect(() => {
        if (avatarMenuOpen) {
            document.addEventListener("mousedown", handleClickOutsideAvatar);
        } else {
            document.removeEventListener("mousedown", handleClickOutsideAvatar);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutsideAvatar);
        };
    }, [avatarMenuOpen]);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer}>
                    {/* Hamburger menu */}
                    <button className={styles.hamburgerButton} aria-label="Open navigation menu" onClick={toggleHistoryPanel}>
                        <span className={styles.hamburgerLine} />
                        <span className={styles.hamburgerLine} />
                        <span className={styles.hamburgerLine} />
                    </button>

                    {/* ABB Logo + divider + App name */}
                    <div className={styles.headerTitleContainer}>
                        <img src={abbLogo} alt="ABB logo" className={styles.abbLogo} />
                        <div className={styles.headerDivider} />
                        <h3 className={styles.headerTitle}>{t("headerTitle")}</h3>
                    </div>

                    {/* Spacer */}
                    <div className={styles.headerSpacer} />

                    <ReportProblemButton username={username} />

                    {/* User avatar */}
                    <div className={styles.avatarWrapper} ref={avatarMenuRef}>
                        <button
                            className={styles.avatarButton}
                            onClick={() => setAvatarMenuOpen(prev => !prev)}
                            aria-label="User menu"
                            aria-expanded={avatarMenuOpen}
                        >
                            {username ? getInitials(username) : "U"}
                        </button>
                        {avatarMenuOpen && (
                            <div className={styles.avatarDropdown}>
                                {username && (
                                    <div className={styles.avatarDropdownUser}>{username}</div>
                                )}
                                <button
                                    className={styles.avatarDropdownItem}
                                    onClick={loggedIn ? handleLogout : handleLogin}
                                >
                                    {loggedIn ? t("logout") : t("login")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className={styles.main} id="main-content">
                <Outlet />
            </main>
        </div>
    );
};

const Layout = () => {
    return (
        <HistoryPanelProvider>
            <LayoutContent />
        </HistoryPanelProvider>
    );
};

export default Layout;
